import { useEffect } from "react";
import { CalendarDays, MapPin, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { Seo } from "../components/seo";
import { PageHero, Section } from "../components/ui";

declare global {
  interface Window {
    Tally?: { loadEmbeds: () => void };
  }
}

const TALLY_EMBED_URL = "https://tally.so/embed/3qop2O?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1";
const TALLY_SCRIPT_URL = "https://tally.so/widgets/embed.js";

function TallyEmbed() {
  useEffect(() => {
    const loadEmbeds = () => window.Tally?.loadEmbeds();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TALLY_SCRIPT_URL}"]`);

    if (window.Tally) {
      loadEmbeds();
      return;
    }

    if (existing) {
      existing.addEventListener("load", loadEmbeds);
      return () => existing.removeEventListener("load", loadEmbeds);
    }

    const script = document.createElement("script");
    script.src = TALLY_SCRIPT_URL;
    script.async = true;
    script.addEventListener("load", loadEmbeds);
    document.head.append(script);
    return () => script.removeEventListener("load", loadEmbeds);
  }, []);

  return (
    <div className="tally-embed-shell">
      <iframe
        className="tally-embed"
        data-tally-src={TALLY_EMBED_URL}
        src={TALLY_EMBED_URL}
        loading="lazy"
        width="100%"
        height="374"
        frameBorder="0"
        title="Stuur je vraag"
        referrerPolicy="strict-origin-when-cross-origin"
      />
      <noscript><p className="tally-noscript">JavaScript staat uit. <a href="https://tally.so/r/3qop2O">Open het contactformulier bij Tally</a>.</p></noscript>
    </div>
  );
}

export default function Contact() {
  return (
    <>
      <Seo title="Contact met STAQL in het Gooi en Almere" description="Bespreek je ICT-vraag met STAQL voor Hilversum, Blaricum, Naarden, Huizen en Almere. Gebruik het formulier of bel 035 369 01 50." />
      <PageHero eyebrow="Contact" title="Vertel wat er beter moet." intro="Je vraag hoeft nog niet technisch of volledig uitgewerkt te zijn. Vertel waar je vastloopt, welke ambitie je hebt of welk werk onnodig veel tijd kost. Dan kijken we samen naar een verstandige volgende stap." />
      <Section>
        <div className="contact-layout">
          <aside className="contact-sidebar">
            <div><span className="contact-sidebar__icon"><CalendarDays aria-hidden="true" /></span><h2>Een eerste gesprek</h2><p>We bespreken je situatie, het gewenste resultaat en of STAQL de juiste partij is om je verder te helpen.</p></div>
            <div className="contact-details"><a href="tel:+31353690150"><Phone aria-hidden="true" /><span><small>Bel direct</small>035 369 01 50</span></a><div><MapPin aria-hidden="true" /><span><small>Gevestigd aan</small>Botterstraat 47<br />1271 XL Huizen</span></div></div>
            <p className="contact-sidebar__note">Een eerste kennismaking is vrijblijvend.</p>
          </aside>
          <section className="contact-form contact-form--tally" id="contactformulier" aria-labelledby="contactformulier-title">
            <div className="contact-form__header"><span className="eyebrow">Kennismaken</span><h2 id="contactformulier-title">Stuur je vraag</h2></div>
            <TallyEmbed />
            <p className="form-privacy">Het formulier wordt verwerkt via Tally. Lees in onze <Link to="/privacy">privacyverklaring</Link> hoe we met je gegevens omgaan.</p>
          </section>
        </div>
      </Section>
      <Section className="section--tint">
        <div className="next-steps"><div><span>01</span><h3>Je vraag komt bij STAQL binnen</h3><p>Via het beveiligde formulier deel je de informatie die nodig is om je vraag te beoordelen.</p></div><div><span>02</span><h3>Youssef bekijkt je situatie</h3><p>We nemen contact op om de belangrijkste context kort te verduidelijken.</p></div><div><span>03</span><h3>We bepalen een volgende stap</h3><p>Is er een goede match, dan spreken we een passende verkenning of aanpak af.</p></div></div>
      </Section>
    </>
  );
}
